import { Divider, Stack, Typography } from '@mui/material';
import { useXaaIdPs } from 'api/idps';
import IdPMethodCard from 'components/IdPsGallery/IdPMethodCard';
import IdpGallery from 'components/IdPsGallery/IdPsGallery';
import SearchInput from 'components/shared/SearchInput';
import { ROUTES } from 'constants/routes';
import { GENERIC_IDP_ID, Method } from 'consts';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import { useFeatureEnabledForConfig } from 'api/configurations';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { S4Feature } from 'types/api';
import { useNav, useStepNavigate } from 'utils/navigation';

// Step 1 of the XAA flow is provider selection - exactly like SSO/SCIM start with IdP
// selection. Providers come from the SAME content catalog (/v1/ssotemplates via
// useXaaIdPs), never fabricated. Mirrors SelectSCIMIdPPage: branded vendor cards (any
// content entry advertising `methods: ['xaa']`) live in the searchable gallery, while
// the generic/manual "configure directly" card is split out below the divider.
const SelectXAAIdPPage: React.FC = () => {
	// Config-aware, not useIsXaaEnabled: a Cross-App Access exchange through a connection classified
	// as authentication only is refused, so the guide must not be reachable by URL either.
	const { enabled: xaaEnabled } = useFeatureEnabledForConfig(S4Feature.Xaa);
	const nav = useNav();
	const { navigateToXaaStep } = useStepNavigate();
	const { xaaIdpID, xaaMethod, isSubTenant } = useIdPConfigContext();
	const { data: xaaIdps } = useXaaIdPs();
	const [idpSearch, setIdpSearch] = useState('');

	useEffect(() => {
		if (!xaaEnabled) {
			nav(ROUTES.home);
		}
	}, [xaaEnabled, nav]);

	const handleSelect = useCallback(
		(idpID: string) => {
			navigateToXaaStep(1, idpID);
		},
		[navigateToXaaStep]
	);

	// The generic/manual XAA card, split out of the gallery the same way SCIM splits its
	// generic card - both key off GENERIC_IDP_ID, whose template carries the generic guide.
	const genericIdP = useMemo(
		() => xaaIdps?.find((idp) => idp.id === GENERIC_IDP_ID),
		[xaaIdps]
	);

	if (!xaaEnabled) {
		return null;
	}

	return (
		<Stack gap={4}>
			<Stack gap={0.5}>
				<Typography variant="h2">
					{intl.tr('xaaConfig.guide.selectIdP.pageTitle')}
				</Typography>
				<Typography>
					{intl.tr('xaaConfig.guide.selectIdP.pageSubheader')}
				</Typography>
			</Stack>
			<Stack gap={3}>
				<SearchInput
					data-testid="idps-gallery-search"
					sx={{ maxWidth: 528, height: 50 }}
					onSearchChange={setIdpSearch}
				/>
				<IdpGallery
					idps={xaaIdps?.filter((idp) => idp !== genericIdP) || []}
					loader={!xaaIdps}
					filterText={idpSearch}
					selectedIdP={xaaIdpID}
					selectedMethod={xaaMethod}
					disabled={isSubTenant}
					onIdPCardClick={handleSelect}
				/>
			</Stack>
			<Divider>{intl.tr('xaaConfig.guide.selectIdP.divider.or')}</Divider>
			<Stack gap={2}>
				<Typography>
					{intl.tr('xaaConfig.guide.selectIdP.custom.message')}
				</Typography>
				<Stack direction="row" gap={3}>
					{genericIdP && (
						<IdPMethodCard
							idp={genericIdP}
							method={Method.Xaa}
							selectedIdP={xaaIdpID}
							disabled={isSubTenant}
							selectedMethod={xaaMethod}
							onClick={() => handleSelect(genericIdP.id || GENERIC_IDP_ID)}
						/>
					)}
				</Stack>
			</Stack>
		</Stack>
	);
};

export default SelectXAAIdPPage;
